import { useEffect, useState } from 'react';

/**
 * Tri et recherche des écrans de liste (ticket #22).
 *
 * Tout part au SERVEUR : ces listes sont paginées (25 lignes), trier ou filtrer
 * ce que le navigateur a reçu ne porterait que sur la page affichée — 25 lignes
 * sur 238 factures. Ces composants ne font donc que porter l'état de la requête.
 */

export type SortDir = 'asc' | 'desc';

export function useSort(initialSort?: string, initialDir: SortDir = 'desc') {
  const [sort, setSort] = useState<string | undefined>(initialSort);
  const [dir, setDir] = useState<SortDir>(initialDir);
  // Premier clic sur une colonne : ascendant (A→Z, plus petit d'abord), ce que
  // l'utilisateur attend d'un nom ou d'un n°. Re-clic : on inverse.
  const toggle = (key: string) => {
    if (sort === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir('asc'); }
  };
  return { sort, dir, toggle };
}

/** En-tête de colonne cliquable. `col` est la clé attendue par la route. */
export function SortTh({
  col, label, sort, dir, onSort, className,
}: {
  col: string;
  label: string;
  sort: string | undefined;
  dir: SortDir;
  onSort: (col: string) => void;
  className?: string;
}) {
  const active = sort === col;
  return (
    <th
      className={`th-sort${active ? ' active' : ''}${className ? ' ' + className : ''}`}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(col)}
      title={`Trier par ${label.toLowerCase()}`}
    >
      {label}<span className="th-arrow">{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

/**
 * Champ de recherche. La frappe reste locale et n'est propagée qu'après une pause :
 * sans ce délai, chaque caractère déclencherait un aller-retour Odoo.
 */
export function SearchInput({
  value, onChange, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState(value);
  // Remise à zéro venue du parent (changement d'onglet) : on suit.
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    if (draft === value) return;
    const t = setTimeout(() => onChange(draft), 350);
    return () => clearTimeout(t);
  }, [draft, value, onChange]);

  return (
    <div className="list-search">
      <input
        className="stock-search"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
      />
      {draft && (
        <button className="list-search-clear" onClick={() => setDraft('')} title="Effacer">×</button>
      )}
    </div>
  );
}

/** Sélecteur de filtre compact (état, paiement…). */
export function FilterSelect<T extends string>({
  value, onChange, options, label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: [T, string][];
  label: string;
}) {
  return (
    <label className="list-filter">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
      </select>
    </label>
  );
}
